/**
 * The colour scales the crown can be painted with.
 *
 * Each map answers a different question, so each has its own range and its
 * own words - but they share one mechanism: a signed number per vertex in
 * millimetres, a set of colour stops, and a rule for when to leave the crown
 * its own colour. Never micrometres: the parameters beside the map are in
 * millimetres, and a scale in another unit is a misreading waiting to happen.
 *
 * Meaning is carried by hue, not by lightness alone, so the maps stay legible
 * to a colour-blind reader and in a screenshot pasted into a chat.
 */

export interface HeatmapStop {
  at: number;
  colour: string;
}

export interface HeatmapScale {
  min: number;
  max: number;
  stops: HeatmapStop[];
  /** Words under the ends and the middle of the legend. */
  words: [string, string, string];
  /** Values that should not be painted at all - the crown keeps its colour. */
  skip: (value: number) => boolean;
}

/**
 * Contacts, against the antagonist or the neighbours. ±0.50 mm, the
 * workbench's own display range, in NuCAD's own colours so the lab and the
 * dentist read the same map (refine_panel.heatmap_colors):
 *   deep red    through the other surface - it will be high, or will not seat
 *   yellow      touching, the contact a dentist wants to see
 *   crown gold  clear by 0.50 mm or more - the bulk of the crown, quiet
 * The in-between stops follow NuCAD's smoothstep on each side of zero.
 */
export const CONTACT_SCALE: HeatmapScale = {
  min: -0.5,
  max: 0.5,
  stops: [
    { at: -0.5, colour: '#990000' },
    { at: -0.375, colour: '#a9260c' },
    { at: -0.25, colour: '#cc7926' },
    { at: -0.125, colour: '#efcc41' },
    { at: 0, colour: '#fff24d' },
    { at: 0.125, colour: '#f8e749' },
    { at: 0.25, colour: '#eacf42' },
    { at: 0.375, colour: '#dbb83a' },
    { at: 0.5, colour: '#d4ad36' },
  ],
  words: ['Through', 'Touching', 'Clear'],
  skip: (value) => Number.isNaN(value),
};

/**
 * Wall thickness, 0 to 2 mm, built around the material's own minimum: red below
 * it, amber just above, green comfortable, blue thick. The minimum moves the
 * whole scale, so "red" always means "thinner than this material allows".
 */
export function thicknessScale(minimum: number): HeatmapScale {
  return {
    min: 0,
    max: 2,
    stops: [
      { at: 0, colour: '#9f1239' },
      { at: Math.max(0.05, minimum - 0.05), colour: '#e11d48' },
      { at: minimum + 0.1, colour: '#f59e0b' },
      { at: minimum + 0.35, colour: '#4ade80' },
      { at: 2, colour: '#3b82f6' },
    ],
    words: ['Thin', `${minimum.toFixed(2)} mm minimum`, 'Thick'],
    skip: (value) => Number.isNaN(value),
  };
}

/**
 * What moved since the last version, ±0.30 mm: blue where material was taken
 * off, orange where it was added. Anything under 0.02 mm is left unpainted -
 * that is noise from re-meshing, not a change anybody made.
 */
export const CHANGE_SCALE: HeatmapScale = {
  min: -0.3,
  max: 0.3,
  stops: [
    { at: -0.3, colour: '#1d4ed8' },
    { at: -0.1, colour: '#60a5fa' },
    { at: 0, colour: '#e5e7eb' },
    { at: 0.1, colour: '#fb923c' },
    { at: 0.3, colour: '#c2410c' },
  ],
  words: ['Removed', 'Unchanged', 'Added'],
  skip: (value) => Number.isNaN(value) || Math.abs(value) < 0.02,
};

function hexToRgb(hex: string): [number, number, number] {
  const value = Number.parseInt(hex.slice(1), 16);
  return [((value >> 16) & 255) / 255, ((value >> 8) & 255) / 255, (value & 255) / 255];
}

/** The colour for one value, as 0-1 sRGB, or null where the crown keeps its own colour. */
export function scaleColour(scale: HeatmapScale, value: number): [number, number, number] | null {
  if (scale.skip(value)) return null;
  const clamped = Math.min(scale.max, Math.max(scale.min, value));
  const stops = scale.stops;
  for (let i = 1; i < stops.length; i++) {
    const hi = stops[i]!;
    if (clamped <= hi.at) {
      const lo = stops[i - 1]!;
      const a = hexToRgb(lo.colour);
      const b = hexToRgb(hi.colour);
      const t = hi.at === lo.at ? 1 : (clamped - lo.at) / (hi.at - lo.at);
      return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
    }
  }
  return hexToRgb(stops[stops.length - 1]!.colour);
}

/** The CSS gradient for a legend, built from the same stops so the two cannot drift. */
export function scaleGradientCss(scale: HeatmapScale): string {
  const span = scale.max - scale.min;
  const stops = scale.stops.map(
    (stop) => `${stop.colour} ${String(Math.round(((stop.at - scale.min) / span) * 100))}%`,
  );
  return `linear-gradient(to right, ${stops.join(', ')})`;
}

// The contact scale under its original names, which the tests and the legend use.
export const HEATMAP_RANGE_MM = CONTACT_SCALE.max;
export const heatmapColour = (distance: number) => scaleColour(CONTACT_SCALE, distance);
export const heatmapGradientCss = () => scaleGradientCss(CONTACT_SCALE);
